// LE FANTÔME DU RECORDMAN (25/07, favorite 2 de l'audit de rétention).
//
// L'idée : quand tu lances une carte, le coureur translucide du détenteur du record court à côté de
// toi, exactement comme il l'a fait. Tu vois où il gagne ses deux secondes, tu recommences. C'est le
// duel permanent, sans avoir besoin que l'autre soit devant son écran — le remède direct au constat
// du 25/07 (« le versus est le vrai plaisir, et il est mort le 22 juillet »).
//
// Pourquoi c'est possible ici sans toucher au moteur : `tick(state, inputs)` reçoit ses commandes de
// l'EXTÉRIEUR. Il suffit donc d'enregistrer la suite des PlayerInput d'une partie et de la rejouer
// sur une seconde partie du même niveau : à moteur déterministe, la course rejouée est au sous-pixel
// près celle qui a été faite. Rien de ce module n'entre dans la simulation ni dans le hash d'état.
//
// Ce fichier ne contient que l'ENCODAGE (pur, testable, partagé client/serveur). Le rejeu vit dans
// client/ghost.ts, le stockage dans server/.
import { type PlayerInput, type UseDir } from './types'

/**
 * Version du FORMAT et du MOTEUR. À incrémenter dès qu'un changement de `shared/sim.ts` (ou de
 * `shared/state.ts`) modifie le déroulé d'une partie à entrées égales : les fantômes enregistrés
 * avant ne rejoueraient plus la même course. Le serveur ne sert jamais un fantôme d'une autre
 * version — il disparaît simplement, ce qui est toujours préférable à un fantôme qui ment.
 */
// ⚠️ RESTÉ À 1 le 26/07, et c'est une décision mesurée, pas un oubli. Allumer la meute change bien
// ce que font les moines — un enregistrement d'avant ne rejouerait plus la même course. Mais après
// mesure (rejeu de vraies courses de la famille contre les deux IA), la meute a été ramenée au
// niveau 05 : les niveaux 01 à 04 sont INCHANGÉS, et ce sont exactement ceux où vivent tous les
// fantômes enregistrés à ce jour. Aucun fantôme n'existe sur 05 et au-delà. Bumper aurait donc
// détruit les seuls fantômes de la famille sans rien protéger.
//
// ⚠️ PASSÉ À 2 le 06/08, et cette fois il le FALLAIT. Le chantier trampoline/eau touche la physique
// elle-même, sur des tuiles qu'on croise partout : fin de bond qui ne recale plus x en l'air, et
// flottaison à la surface de l'eau. À entrées égales, une course d'avant ne se rejoue plus au
// sous-pixel près — et un fantôme qui ment est pire que pas de fantôme. Les enregistrements
// d'avant ne sont plus servis (le serveur filtre sur la version) ; les prochains repartent propres.
export const GHOST_VERSION = 2

/** Plafond défensif du nombre de ticks enregistrés (~16 minutes à 30 Hz). Au-delà, on n'enregistre
 *  pas : un fantôme d'un quart d'heure n'intéresse personne et pèserait pour rien. */
export const GHOST_MAX_TICKS = 30_000

/** Plafond défensif de la taille encodée acceptée par le serveur (caractères). */
export const GHOST_MAX_CHARS = 16_000

// Ordre FIGÉ des directions d'usage (index dans le code compact). Ne jamais réordonner sans bumper
// GHOST_VERSION : les enregistrements existants relisent ces index.
const DIRS: readonly UseDir[] = [null, 'up', 'down', 'left', 'right']

/** Code compact d'une entrée : 8 drapeaux en bits 0-7, direction d'usage en bits 8+. Pur. */
export function inputCode(i: PlayerInput): number {
  const flags =
    (i.left ? 1 : 0) | (i.right ? 2 : 0) | (i.up ? 4 : 0) | (i.down ? 8 : 0) |
    (i.digL ? 16 : 0) | (i.digR ? 32 : 0) | (i.use ? 64 : 0) | (i.dash ? 128 : 0)
  const dir = Math.max(0, DIRS.indexOf(i.useDir))
  return flags | (dir << 8)
}

/** L'entrée que représente un code compact. Inverse exact de inputCode. Pur. */
export function codeInput(code: number): PlayerInput {
  return {
    left: (code & 1) !== 0,
    right: (code & 2) !== 0,
    up: (code & 4) !== 0,
    down: (code & 8) !== 0,
    digL: (code & 16) !== 0,
    digR: (code & 32) !== 0,
    use: (code & 64) !== 0,
    dash: (code & 128) !== 0,
    useDir: DIRS[code >> 8] ?? null,
  }
}

/**
 * Encode une suite d'entrées en texte compact. Compression par PLAGES (RLE) : dans une vraie partie
 * on tient une touche pendant des dizaines de ticks, donc une minute de course tient en quelques
 * centaines d'octets. Format : jetons séparés par '.', chacun « code » (une fois) ou « code*n »
 * (n fois), tout en base 36. Pur, sans horloge ni hasard.
 */
export function encodeInputs(inputs: readonly PlayerInput[]): string {
  const out: string[] = []
  let run = 0
  let cur = -1
  const flush = (): void => {
    if (run === 0) return
    out.push(run === 1 ? cur.toString(36) : `${cur.toString(36)}*${run.toString(36)}`)
  }
  for (const i of inputs) {
    const c = inputCode(i)
    if (c === cur) { run++; continue }
    flush()
    cur = c
    run = 1
  }
  flush()
  return out.join('.')
}

/** Forme d'un jeton valide : « code » ou « code*répétitions », en base 36 (chiffres et minuscules). */
const TOKEN_RE = /^[0-9a-z]+(\*[0-9a-z]+)?$/

/** Décode ce qu'a produit encodeInputs. Un jeton illisible interrompt la lecture (on rend ce qui a
 *  pu être lu) : un fantôme tronqué s'arrête en route, il ne fait jamais planter la partie. */
export function decodeInputs(s: string): PlayerInput[] {
  const out: PlayerInput[] = []
  if (s.length === 0) return out
  for (const token of s.split('.')) {
    // Validation STRICTE avant parseInt : parseInt('zzz!!!', 36) rendrait 46655 sans broncher, et un
    // enregistrement abîmé produirait des entrées inventées au lieu de s'arrêter. Un fantôme tronqué
    // s'immobilise, ce qui se voit ; un fantôme qui invente des touches ment sur la course à battre.
    if (!TOKEN_RE.test(token)) break
    const star = token.indexOf('*')
    const code = parseInt(star === -1 ? token : token.slice(0, star), 36)
    const n = star === -1 ? 1 : parseInt(token.slice(star + 1), 36)
    if (!Number.isFinite(code) || !Number.isFinite(n) || n < 1) break
    const input = codeInput(code)
    for (let k = 0; k < n && out.length < GHOST_MAX_TICKS; k++) out.push(input)
  }
  return out
}
